import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button, Card } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import {
  DetailDrawer,
  KanbanBoard,
  PipeSelector,
  useKanban,
  type PipeOption,
} from '@/modules/kanban';
import { JURIDICO_PIPES } from '@/modules/juridico/data';
import {
  juridicoCardFieldMap,
  juridicoDetailFieldMap,
} from '@/modules/juridico/fieldMap';

export const Route = createFileRoute('/_authed/juridico')({
  component: JuridicoPage,
});

function JuridicoPage() {
  const [pipe, setPipe] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const { data, isLoading, error } = useKanban('juridico', pipe ?? undefined);

  if (!pipe) {
    return (
      <PageShell
        eyebrow="Negócios"
        title="Jurídico"
        lead="Escolha um pipe pra abrir o kanban."
      >
        <PipeSelector
          pipes={JURIDICO_PIPES.map(
            (p): PipeOption => ({
              id: p.id,
              label: p.label,
              icon: p.icon,
              active: false,
              iconTone: 'indigo',
              ...(p.disabled !== undefined && { disabled: p.disabled }),
            }),
          )}
          selected={null}
          onSelect={setPipe}
        />
      </PageShell>
    );
  }

  const pipeMeta = JURIDICO_PIPES.find((p) => p.id === pipe);

  return (
    <PageShell
      eyebrow={`Jurídico · ${pipeMeta?.label ?? pipe}`}
      title={pipeMeta?.label ?? 'Kanban'}
      lead={pipeMeta?.description}
      back={
        <Button variant="ghost" onClick={() => setPipe(null)}>
          <ArrowLeft size={14} strokeWidth={1.5} />
          Voltar
        </Button>
      }
    >
      <Stack gap="md">
        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando kanban…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar kanban.'}
            </p>
          </Card>
        ) : data ? (
          <>
            <KanbanBoard
              data={data}
              fieldMap={juridicoCardFieldMap}
              dominio="juridico"
              pipe={pipe}
              onCardSelect={setSelectedCard}
            />
            <DetailDrawer
              dominio="juridico"
              pipe={pipe}
              cardId={selectedCard}
              columns={data.colunas}
              cardFieldMap={juridicoCardFieldMap}
              detailFieldMap={juridicoDetailFieldMap}
              onClose={() => setSelectedCard(null)}
            />
          </>
        ) : null}
      </Stack>
    </PageShell>
  );
}
